import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { accessRequestService } from '../services/api';

function AccessRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await accessRequestService.getRequestById(id);
      console.log('Dados da solicitação:', data);
      // O DTO agora retorna os dados diretamente, não mais dentro de um campo "request"
      setRequest(data);
    } catch (err) {
      console.error('Erro ao carregar solicitação:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data ||
        'Erro ao carregar solicitação'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (cancelReason.length < 10 || cancelReason.length > 200) {
      setError('O motivo deve ter entre 10 e 200 caracteres');
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      await accessRequestService.cancelRequest(id, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
      loadRequest();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data ||
        'Erro ao cancelar solicitação'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenew = async () => {
    setActionLoading(true);
    setError('');
    try {
      await accessRequestService.renewRequest(id);
      loadRequest();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data ||
        'Erro ao renovar solicitação'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      PENDING: 'status-pending',
      APPROVED: 'status-approved',
      REJECTED: 'status-rejected',
      CANCELED: 'status-canceled',
      EXPIRED: 'status-expired',
    };
    return `badge status-badge ${statusMap[status] || 'bg-secondary'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR');
  };

  if (loading) {
    return (
      <div className="container">
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="container">
        <div className="alert alert-danger mt-4" role="alert">
          {error}
        </div>
        <Link to="/requests" className="btn btn-secondary">
          Voltar para Lista
        </Link>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="container">
        <div className="alert alert-warning mt-4" role="alert">
          Solicitação não encontrada
        </div>
        <Link to="/requests" className="btn btn-secondary">
          Voltar para Lista
        </Link>
      </div>
    );
  }

  const canCancel = request.status === 'PENDING' || request.status === 'APPROVED';
  const canRenew = request.status === 'APPROVED' || request.status === 'EXPIRED';

  return (
    <div className="container">
      <div className="mb-3">
        <Link to="/requests" className="btn btn-outline-secondary">
          ← Voltar para Lista
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Detalhes da Solicitação #{request.id}</h4>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Status:</strong>
              <div>
                <span className={getStatusBadgeClass(request.status)}>
                  {request.status}
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <strong>Urgente:</strong>
              <div>
                {request.urgent ? (
                  <span className="badge bg-warning text-dark">Sim</span>
                ) : (
                  <span className="badge bg-secondary">Não</span>
                )}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Data de Criação:</strong>
              <div>{formatDate(request.requestDate)}</div>
            </div>
            <div className="col-md-6">
              <strong>Data de Expiração:</strong>
              <div>{formatDate(request.expirationDate)}</div>
            </div>
          </div>

          {request.justification && (
            <div className="mb-3">
              <strong>Justificativa:</strong>
              <div className="border rounded p-3 bg-light">
                {request.justification}
              </div>
            </div>
          )}

          {request.modules && request.modules.length > 0 && (
            <div className="mb-3">
              <strong>Módulos Solicitados:</strong>
              <ul className="list-group mt-2">
                {request.modules.map((module) => (
                  <li key={module.id} className="list-group-item">
                    <strong>{module.name}</strong>
                    {module.description && (
                      <div className="text-muted small">{module.description}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {request.denialReason && (
            <div className="mb-3">
              <strong>Motivo do Cancelamento/Rejeição:</strong>
              <div className="border rounded p-3 bg-light">
                {request.denialReason}
              </div>
            </div>
          )}

          <div className="d-flex gap-2 mt-4">
            {canCancel && (
              <button
                className="btn btn-danger"
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading}
              >
                Cancelar Solicitação
              </button>
            )}
            {canRenew && (
              <button
                className="btn btn-success"
                onClick={handleRenew}
                disabled={actionLoading}
              >
                {actionLoading ? 'Renovando...' : 'Renovar Acesso'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Cancelamento */}
      {showCancelModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cancelar Solicitação</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                    setError('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="cancelReason" className="form-label">
                    Motivo do Cancelamento
                  </label>
                  <textarea
                    className="form-control"
                    id="cancelReason"
                    rows="4"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    minLength={10}
                    maxLength={200}
                    placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)"
                  />
                  <small className="text-muted">
                    {cancelReason.length}/200 caracteres
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                    setError('');
                  }}
                  disabled={actionLoading}
                >
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCancel}
                  disabled={actionLoading || cancelReason.length < 10}
                >
                  {actionLoading ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccessRequestDetails;

